import { useEffect, useRef, useState } from "react";
import { Zap, ShieldCheck, ShieldOff, Gauge, Activity } from "lucide-react";
import { useLang } from "../i18n";
import { Reveal, Eyebrow } from "./common";
import { SpdaScene } from "../lib/spdaScene";

export default function Spda3D() {
    const { t } = useLang();
    const canvasRef = useRef(null);
    const sectionRef = useRef(null);
    const sceneRef = useRef(null);
    const [kv, setKv] = useState(400);
    const [shield, setShield] = useState(true);
    const [tele, setTele] = useState({
        ka: null,
        pv: null,
        shield: true,
        strikes: 0,
    });
    const [flashKey, setFlashKey] = useState(0);

    useEffect(() => {
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const scene = new SpdaScene(canvasRef.current, {
            auto: !reduced,
            onStrike: (info) =>
                setTele((p) => ({ ...info, strikes: p.strikes + 1 })),
        });
        sceneRef.current = scene;

        const container = canvasRef.current.parentElement;
        const ro = new ResizeObserver(() => {
            scene.resize(container.clientWidth, container.clientHeight);
        });
        ro.observe(container);
        scene.resize(container.clientWidth, container.clientHeight);

        const io = new IntersectionObserver(
            ([e]) => (scene.inView = e.isIntersecting),
            { threshold: 0.12 }
        );
        io.observe(sectionRef.current);

        return () => {
            scene.dispose();
            ro.disconnect();
            io.disconnect();
        };
    }, []);

    useEffect(() => void sceneRef.current?.setShield(shield), [shield]);
    useEffect(() => void sceneRef.current?.setKv(kv), [kv]);

    const trigger = () => {
        sceneRef.current?.strike();
    };

    const safety = tele.shield
        ? 99
        : Math.max(35, Math.round(92 - (tele.pv ?? kv) / 40));

    return (
        <section id="spda" ref={sectionRef} className="relative py-24 md:py-32 bg-deep overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(255,230,0,0.06),transparent_60%)] pointer-events-none" />
            <p className="pointer-events-none select-none absolute -bottom-10 left-0 font-display font-extrabold text-[26vw] leading-none text-outline-faint opacity-40">
                5419
            </p>

            <div className="relative max-w-7xl mx-auto px-5 md:px-10">
                <Reveal className="mb-12 max-w-2xl">
                    <Eyebrow>{t.spda.eyebrow}</Eyebrow>
                    <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
                        {t.spda.title}
                    </h2>
                    <p className="mt-4 text-slate-400">{t.spda.sub}</p>
                </Reveal>

                <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6 items-stretch">
                    <Reveal className="relative">
                        <div
                            className="relative rounded-2xl border border-white/10 bg-obsidian overflow-hidden h-[380px] md:h-[520px]"
                            data-testid="spda-3d-canvas"
                        >
                            <canvas ref={canvasRef} className="block w-full h-full" />
                            <div
                                key={flashKey}
                                className={`absolute inset-0 bg-volt pointer-events-none ${
                                    flashKey ? "strike-flash opacity-0" : "opacity-0"
                                }`}
                            />
                            <div className="absolute top-4 left-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 bg-obsidian/70 backdrop-blur px-3 py-1.5 rounded-full border border-white/10">
                                <span className="w-1.5 h-1.5 rounded-full bg-volt" />
                                NBR 5419 · SPDA
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.15} className="flex flex-col gap-4">
                        <div className="rounded-2xl border border-white/10 bg-surface/70 backdrop-blur-sm p-6 md:p-7">
                            <button
                                data-testid="spda-trigger-lightning-button"
                                onClick={trigger}
                                className="group w-full inline-flex items-center justify-center gap-3 bg-volt text-obsidian font-bold px-6 py-4 rounded-full hover:shadow-[0_0_40px_rgba(255,230,0,0.5)] hover:-translate-y-0.5 transition-all"
                            >
                                <Zap className="w-5 h-5 group-hover:rotate-12 group-hover:scale-110 transition-transform" />
                                {t.spda.strike}
                            </button>

                            <div className="mt-6 flex items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    {shield ? (
                                        <ShieldCheck className="w-5 h-5 text-volt" />
                                    ) : (
                                        <ShieldOff className="w-5 h-5 text-slate-500" />
                                    )}
                                    <div>
                                        <p className="text-sm font-semibold">
                                            {t.spda.shieldLabel}
                                        </p>
                                        <p className="text-xs text-slate-500 max-w-[220px]">
                                            {t.spda.shieldDesc}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    data-testid="spda-shield-toggle"
                                    role="switch"
                                    aria-checked={shield}
                                    onClick={() => setShield(!shield)}
                                    className={`relative w-13 h-7 shrink-0 rounded-full transition-colors w-[52px] ${
                                        shield ? "bg-volt" : "bg-slate-700"
                                    }`}
                                >
                                    <span
                                        className={`absolute top-0.5 w-6 h-6 rounded-full bg-obsidian transition-transform ${
                                            shield ? "translate-x-[24px]" : "translate-x-0.5"
                                        }`}
                                    />
                                </button>
                            </div>

                            <div className="mt-7">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="flex items-center gap-2 text-sm font-semibold">
                                        <Gauge className="w-4 h-4 text-volt" />
                                        {t.spda.voltage}
                                    </span>
                                    <span
                                        data-testid="spda-voltage-readout"
                                        className="font-mono text-sm text-volt"
                                    >
                                        {kv} kV
                                    </span>
                                </div>
                                <input
                                    data-testid="spda-voltage-slider"
                                    type="range"
                                    min="100"
                                    max="1000"
                                    step="50"
                                    value={kv}
                                    onChange={(e) => setKv(Number(e.target.value))}
                                    className="volt-slider"
                                    style={{
                                        ["--fill"]: `${((kv - 100) / 900) * 100}%`,
                                    }}
                                    aria-label={t.spda.voltage}
                                />
                                <div className="flex justify-between font-mono text-[10px] text-slate-500 mt-2">
                                    <span>100 kV</span>
                                    <span>1.000 kV</span>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-2xl border border-white/10 bg-surface/70 backdrop-blur-sm p-6 md:p-7 font-mono">
                            <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-5">
                                <Activity className="w-3.5 h-3.5 text-volt" />
                                {t.spda.telemetry.title}
                            </p>
                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between" data-testid="spda-telemetry-voltage">
                                    <span className="text-slate-500">{t.spda.telemetry.kv}</span>
                                    <span className="text-volt">
                                        {tele.pv ? `${tele.pv} kV` : "—"}
                                    </span>
                                </div>
                                <div className="flex justify-between" data-testid="spda-telemetry-current">
                                    <span className="text-slate-500">{t.spda.telemetry.ka}</span>
                                    <span className="text-volt">{tele.ka ? `${tele.ka} kA` : "—"}</span>
                                </div>
                                <div className="flex justify-between" data-testid="spda-telemetry-safety">
                                    <span className="text-slate-500">{t.spda.telemetry.safety}</span>
                                    <span className={tele.shield ? "text-emerald-400" : "text-red-400"}>
                                        {tele.strikes ? `${safety}%` : "—"}
                                    </span>
                                </div>
                                <div className="flex justify-between" data-testid="spda-telemetry-ground">
                                    <span className="text-slate-500">{t.spda.telemetry.groundLabel}</span>
                                    <span className="text-slate-300">{t.spda.telemetry.ground}</span>
                                </div>
                                <div className="flex justify-between" data-testid="spda-telemetry-strikes">
                                    <span className="text-slate-500">{t.spda.telemetry.strikes}</span>
                                    <span className="text-slate-300">{tele.strikes}</span>
                                </div>
                            </div>
                            <div
                                data-testid="spda-status"
                                className={`mt-5 rounded-lg border px-4 py-2.5 text-center text-xs font-semibold tracking-wide ${
                                    tele.shield
                                        ? "border-emerald-400/40 text-emerald-400 bg-emerald-400/10"
                                        : "border-red-400/40 text-red-400 bg-red-400/10"
                                }`}
                            >
                                {tele.shield ? t.spda.safe : t.spda.unsafe}
                            </div>
                        </div>

                        <p className="text-[11px] text-slate-500 leading-relaxed px-1">
                            {t.spda.note}
                        </p>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
